// Funções puras da escolha de arquivos/pastas (input `webkitdirectory` e arrastar-e-soltar):
// limpeza do caminho relativo e varredura recursiva das entradas do DataTransfer. Sem DOM — os
// tipos de entrada são estruturais para os testes (Vitest, ambiente node) usarem objetos falsos.
// Usado pelo Dropzone (documentos, importação de extratos nas fases seguintes).

/** Arquivo escolhido + caminho relativo dentro da pasta enviada ('' quando veio solto). */
export interface PickedFile<F = File> {
  file: F;
  /** Ex.: "Armas/CRAF/pistola.pdf" (inclui o nome do arquivo). */
  relativePath: string;
}

/**
 * Normaliza o caminho relativo: barras invertidas viram "/", tira a "/" inicial (do
 * `entry.fullPath`), barras repetidas e segmentos ".".
 */
export const cleanRelativePath = (path: string): string =>
  (path ?? '')
    .replace(/\\/g, '/')
    .split('/')
    .filter((s) => s !== '' && s !== '.')
    .join('/');

/** Extensão em minúsculas, sem o ponto. "foto.JPG" → "jpg"; "LEIAME" → "". */
export const fileExtension = (name: string): string => {
  const base = name.split('/').pop() ?? name;
  const dot = base.lastIndexOf('.');
  return dot > 0 && dot < base.length - 1 ? base.slice(dot + 1).toLowerCase() : '';
};

// ---------------------------------------------------------------- varredura de pastas

/** Forma estrutural de `FileSystemEntry` (só o que a varredura usa). */
export interface FsEntryLike {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
}

export interface FsFileEntryLike<F> extends FsEntryLike {
  file(success: (file: F) => void, error?: (err: unknown) => void): void;
}

export interface FsDirectoryReaderLike {
  readEntries(success: (entries: FsEntryLike[]) => void, error?: (err: unknown) => void): void;
}

export interface FsDirectoryEntryLike extends FsEntryLike {
  createReader(): FsDirectoryReaderLike;
}

/**
 * Lê TODAS as entradas de uma pasta. `readEntries` devolve no máximo ~100 por chamada e
 * precisa ser chamado de novo até voltar um lote vazio.
 */
export const readAllDirectoryEntries = async (dir: FsDirectoryEntryLike): Promise<FsEntryLike[]> => {
  const reader = dir.createReader();
  const all: FsEntryLike[] = [];
  for (;;) {
    const batch = await new Promise<FsEntryLike[]>((resolve, reject) => reader.readEntries(resolve, reject));
    if (batch.length === 0) return all;
    all.push(...batch);
  }
};

const readEntryFile = <F>(entry: FsFileEntryLike<F>): Promise<F> =>
  new Promise<F>((resolve, reject) => entry.file(resolve, reject));

export interface CollectResult<F> {
  files: PickedFile<F>[];
  /** Entradas que não puderam ser lidas (caminho), p.ex. caminho longo demais no Windows. */
  unreadable: string[];
}

/**
 * Percorre as entradas soltas (arquivos e pastas misturados), descendo nas subpastas.
 * O caminho relativo vem de `entry.fullPath` sem a "/" inicial — para um arquivo solto é só o
 * nome; dentro de uma pasta, "Pasta/Sub/foto.jpg" (igual a `File.webkitRelativePath`).
 * `onProgress` recebe quantos arquivos já foram encontrados.
 */
export const collectEntries = async <F>(
  roots: FsEntryLike[],
  onProgress?: (found: number) => void,
): Promise<CollectResult<F>> => {
  const files: PickedFile<F>[] = [];
  const unreadable: string[] = [];
  const stack: FsEntryLike[] = [...roots].reverse();
  while (stack.length > 0) {
    const entry = stack.pop() as FsEntryLike;
    const path = cleanRelativePath(entry.fullPath || entry.name);
    if (entry.isDirectory) {
      try {
        const children = await readAllDirectoryEntries(entry as FsDirectoryEntryLike);
        for (let i = children.length - 1; i >= 0; i--) stack.push(children[i]);
      } catch {
        unreadable.push(path);
      }
      continue;
    }
    if (!entry.isFile) continue;
    try {
      const file = await readEntryFile(entry as FsFileEntryLike<F>);
      files.push({ file, relativePath: path });
      if (onProgress && files.length % 100 === 0) onProgress(files.length);
    } catch {
      unreadable.push(path);
    }
  }
  onProgress?.(files.length);
  return { files, unreadable };
};
